import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';
import BulkUpload from '../components/common/BulkUpload';
import { useAuth } from '../context/AuthContext';
import { useDebounce } from '../hooks/useDebounce';
import { useSavedFilters } from '../hooks/useSavedFilters';
import EntityHistory from '../components/common/EntityHistory';
import PODocumentParser from '../components/common/PODocumentParser';

const STATUSES = ['new', 'quoted', 'negotiating', 'won', 'lost', 'on_hold'];
const KANBAN_COLS = [
  { key: 'new', label: 'New', bg: '#dbeafe', accent: '#1d4ed8' },
  { key: 'quoted', label: 'Quoted', bg: '#ede9fe', accent: '#7c3aed' },
  { key: 'negotiating', label: 'Negotiating', bg: '#fef3c7', accent: '#d97706' },
  { key: 'won', label: 'Won', bg: '#d1fae5', accent: '#059669' },
  { key: 'lost', label: 'Lost', bg: '#fee2e2', accent: '#dc2626' },
  { key: 'on_hold', label: 'On Hold', bg: '#f1f5f9', accent: '#64748b' },
];
const TYPES = ['product', 'cro_service', 'custom_synthesis', 'method_development', 'other'];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'JPY', 'AED', 'SGD'];
const ORDER_TYPES = [
  { value: 'invoice', label: 'Tax Invoice' },
  { value: 'proforma_invoice', label: 'Proforma Invoice' },
  { value: 'purchase_order', label: 'Purchase Order' },
];
const statusColors = { new: 'info', quoted: 'primary', negotiating: 'warning', won: 'success', lost: 'error', on_hold: 'neutral' };
const typeLabels = { product: 'Product', cro_service: 'CRO Service', custom_synthesis: 'Custom Synthesis', method_development: 'Method Dev', other: 'Other' };

const fmt = (n) => n ? Number(n).toLocaleString('en-IN') : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const emptyConvert = () => ({ order_type: 'invoice', currency: 'USD', quote_number: '', quote_date: '', po_number: '', po_date: '', expected_delivery: '', notes: '', items: [] });

function LeadScoreBadge({ score, grade }) {
  const colors = {
    A: { bg: '#dcfce7', text: '#15803d', border: '#86efac' },
    B: { bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
    C: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
    D: { bg: '#fed7aa', text: '#c2410c', border: '#fdba74' },
    F: { bg: '#fee2e2', text: '#dc2626', border: '#fca5a5' },
  };
  const c = colors[grade] || colors.F;
  return (
    <span title={`Lead score: ${score}/100`} style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      padding: '1px 7px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 700,
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
    }}>
      {grade} · {score}
    </span>
  );
}

export default function Inquiries() {
  const { user, isManager } = useAuth();
  const [data, setData] = useState({ data: [], total: 0, page: 1, limit: 15 });
  const [loading, setLoading] = useState(true);
  const [inqScores, setInqScores] = useState({});
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const { presets: filterPresets, save: saveFilter, remove: removeFilter } = useSavedFilters('inquiries');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'kanban'
  const [kanbanData, setKanbanData] = useState([]);
  const [kanbanLoading, setKanbanLoading] = useState(false);
  const [viewModal, setViewModal] = useState({ open: false, inquiry: null });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [convertModal, setConvertModal] = useState({ open: false, inquiry: null, form: emptyConvert(), saving: false, error: '' });
  const [catalogItems, setCatalogItems] = useState([]);

  useEffect(() => {
    api.get('/catalog/active').then(r => setCatalogItems(r.data.data || [])).catch(() => { });
  }, []);

  const debouncedSearch = useDebounce(search, 300);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/inquiries', {
        params: { page, limit: 15, search: debouncedSearch || undefined, status: statusFilter || undefined },
      });
      setData(res);
    } finally { setLoading(false); }
  }, [page, debouncedSearch, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/lead-scoring/inquiries').then(r => {
      const map = {};
      (r.data.data || []).forEach(s => { map[s.id] = s; });
      setInqScores(map);
    }).catch(() => { });
  }, [data.data]);

  const loadKanban = useCallback(async () => {
    setKanbanLoading(true);
    try {
      const { data: res } = await api.get('/inquiries', { params: { limit: 500, search: debouncedSearch || undefined } });
      setKanbanData(res.data || []);
    } finally { setKanbanLoading(false); }
  }, [debouncedSearch]);

  useEffect(() => { if (viewMode === 'kanban') loadKanban(); }, [viewMode, loadKanban]);

  const openView = async (inq) => {
    const { data: res } = await api.get(`/inquiries/${inq.id}`);
    setViewModal({ open: true, inquiry: res.data });
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/inquiries/${deleteConfirm.id}`);
      setDeleteConfirm(null); load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to freeze inquiry.');
    }
  };

  const handleUnfreeze = async (id) => {
    try { await api.patch(`/inquiries/${id}/unfreeze`); load(); }
    catch (err) { alert(err.response?.data?.message || 'Failed to unfreeze.'); }
  };

  const openConvert = async (inq) => {
    const { data: res } = await api.get(`/inquiries/${inq.id}`);
    const full = res.data;
    // Use quote items if they exist; fall back to single-item legacy behaviour
    let items = [];
    if (Array.isArray(full.items) && full.items.length > 0) {
      items = full.items.map(it => ({
        catalog_item_id: it.catalog_item_id,
        item_name: it.item_name || '',
        catalog_number: it.catalog_number || '',
        quantity: it.quantity || 1,
        price: it.price || 0,
        pack_size: it.pack_size || '',
        discount_percent: it.discount_percent || 0,
      }));
    } else if (full.catalog_item_id) {
      items = [{ catalog_item_id: full.catalog_item_id, item_name: full.catalog_item_name, catalog_number: full.catalog_number, quantity: full.quantity_requested || 1, price: full.quote_amount || 0, pack_size: '', discount_percent: 0 }];
    }
    const quoteDate = full.created_at ? new Date(full.created_at).toISOString().split('T')[0] : '';
    setConvertModal({
      open: true, inquiry: full, saving: false, error: '',
      form: { order_type: 'invoice', currency: full.currency || 'USD', quote_number: full.inquiry_number || '', quote_date: quoteDate, po_number: '', po_date: '', expected_delivery: '', notes: full.notes || '', items },
    });
  };

  const handleConvert = async () => {
    const { inquiry, form } = convertModal;
    if (!form.items.length) {
      setConvertModal(m => ({ ...m, error: 'Add at least one item to create an order.' }));
      return;
    }
    setConvertModal(m => ({ ...m, saving: true, error: '' }));
    try {
      const payload = {
        client_id: inquiry.client_id,
        contact_id: inquiry.contact_id || undefined,
        user_id: user.id,
        inquiry_id: inquiry.id,
        quote_number: form.quote_number || undefined,
        quote_date: form.quote_date || undefined,
        po_number: form.po_number || undefined,
        po_date: form.po_date || undefined,
        currency: form.currency,
        order_type: form.order_type,
        expected_delivery: form.expected_delivery || undefined,
        notes: form.notes || undefined,
        items: form.items.map(it => ({ catalog_item_id: it.catalog_item_id, pack_size: it.pack_size || undefined, quantity: Number(it.quantity), price: Number(it.price), discount_percent: Number(it.discount_percent) || 0 })),
      };
      const { data: orderRes } = await api.post('/orders', payload);
      await api.put(`/inquiries/${inquiry.id}`, { status: 'won' });
      setConvertModal({ open: false, inquiry: null, form: emptyConvert(), saving: false, error: '' });
      load();
      alert(`Order created: ${orderRes.data?.order_number || 'Success'}. Inquiry marked as Won.`);
    } catch (err) {
      setConvertModal(m => ({ ...m, saving: false, error: err?.response?.data?.error || err.message || 'Failed to create order.' }));
    }
  };

  const updateConvertItem = (idx, field, value) => {
    setConvertModal(m => {
      const items = [...m.form.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...m, form: { ...m.form, items } };
    });
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Inquiries &amp; Quotes</h1>
          <p className="page-subtitle">Track client inquiries, quotations, and deal progress</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* View toggle */}
          <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
            <button onClick={() => setViewMode('table')}
              style={{ padding: '7px 14px', border: 'none', background: viewMode === 'table' ? '#1d4ed8' : '#fff', color: viewMode === 'table' ? '#fff' : '#64748b', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}>
              ☰ Table
            </button>
            <button onClick={() => setViewMode('kanban')}
              style={{ padding: '7px 14px', border: 'none', borderLeft: '1px solid #e2e8f0', background: viewMode === 'kanban' ? '#1d4ed8' : '#fff', color: viewMode === 'kanban' ? '#fff' : '#64748b', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}>
              ⬛ Kanban
            </button>
          </div>
          <button className="btn btn-secondary" onClick={() => setBulkOpen(true)}>
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
            Bulk Upload
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '14px 16px' }}>
          <div className="search-bar">
            <div className="search-input-wrap">
              <span className="search-icon">
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              </span>
              <input className="form-control" placeholder="Search inquiry number, client, subject…"
                value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="form-control form-select" style={{ width: 160 }} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              {STATUSES.map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
            {(search || statusFilter) && (
              <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setStatusFilter(''); setPage(1); }}>Clear</button>
            )}
            {(search || statusFilter) && (
              <button className="btn btn-ghost btn-sm" onClick={() => {
                const name = window.prompt('Filter name:', '');
                if (name && name.trim()) saveFilter(name.trim(), { search, statusFilter });
              }} title="Save current filter">
                Save Filter
              </button>
            )}
          </div>
          {filterPresets.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
              {filterPresets.map(p => (
                <span key={p.name} style={{ background: 'var(--primary-lighter, #eff6ff)', color: 'var(--primary)', borderRadius: 20, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid var(--primary-light, #bfdbfe)' }}>
                  <span onClick={() => { setSearch(p.filters.search || ''); setStatusFilter(p.filters.statusFilter || ''); setPage(1); }}>{p.name}</span>
                  <span onClick={() => removeFilter(p.name)} style={{ marginLeft: 2, opacity: 0.7, fontWeight: 700, fontSize: '0.8rem', lineHeight: 1 }}>&times;</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Kanban Board ──────────────────────────────────────── */}
      {viewMode === 'kanban' && (
        <div style={{ overflowX: 'auto', paddingBottom: 16 }}>
          {kanbanLoading ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading pipeline...</div>
          ) : (
            <div style={{ display: 'flex', gap: 14, minWidth: 900 }}>
              {KANBAN_COLS.map(col => {
                const cards = kanbanData.filter(inq => inq.status === col.key && inq.is_active !== 0);
                const totalValue = cards.reduce((s, c) => s + parseFloat(c.quote_amount || 0), 0);
                return (
                  <div key={col.key} style={{ flex: '0 0 190px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* Column header */}
                    <div style={{ background: col.bg, borderRadius: 10, padding: '10px 14px', borderTop: `3px solid ${col.accent}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.82rem', color: col.accent }}>{col.label}</span>
                        <span style={{ background: col.accent, color: '#fff', borderRadius: 12, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700 }}>{cards.length}</span>
                      </div>
                      {totalValue > 0 && (
                        <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 3 }}>
                          {cards[0]?.currency || 'USD'} {totalValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </div>
                      )}
                    </div>
                    {/* Cards */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 100 }}>
                      {cards.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '20px 0', color: '#cbd5e1', fontSize: '0.78rem' }}>No inquiries</div>
                      )}
                      {cards.map(inq => (
                        <div key={inq.id} onClick={() => openView(inq)}
                          style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', cursor: 'pointer', transition: 'box-shadow 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}
                          onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.12)'}
                          onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,.05)'}>
                          <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#1d4ed8', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                            {inq.inquiry_number}
                            {inqScores[inq.id] && <LeadScoreBadge score={inqScores[inq.id].score} grade={inqScores[inq.id].grade} />}
                          </div>
                          <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#0f172a', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{inq.client_name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 6 }}>{inq.subject}</div>
                          {inq.quote_amount && (
                            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#059669' }}>
                              {inq.currency || 'USD'} {fmt(inq.quote_amount)}
                            </div>
                          )}
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 4 }}>{fmtDate(inq.created_at)}</div>
                          {inq.assigned_to_name && (
                            <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 3 }}>
                              <span style={{ background: '#f1f5f9', borderRadius: 4, padding: '1px 6px' }}>{inq.assigned_to_name}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Table */}
      {viewMode === 'table' && <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Inquiry #</th>
                <th>Client</th>
                <th>Subject</th>
                <th>Type</th>
                <th>Quote</th>
                <th>Status</th>
                <th>Assigned To</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9}><div className="loading-overlay"><div className="spinner" /></div></td></tr>}
              {!loading && data.data.length === 0 && (
                <tr><td colSpan={9}><div className="empty-state"><p>No inquiries found.</p></div></td></tr>
              )}
              {!loading && data.data.map(inq => (
                <tr key={inq.id}>
                  <td style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.82rem' }}>
                    {inq.inquiry_number}
                    {inq.is_active === 0 && <span className="badge badge-neutral" style={{ fontSize: '0.62rem', marginLeft: 6 }}>Frozen</span>}
                    {inqScores[inq.id] && <span style={{ marginLeft: 6 }}><LeadScoreBadge score={inqScores[inq.id].score} grade={inqScores[inq.id].grade} /></span>}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{inq.client_name}</div>
                    {inq.contact_name && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{inq.contact_name}</div>}
                  </td>
                  <td style={{ maxWidth: 180 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inq.subject}</div>
                  </td>
                  <td><span className="badge badge-neutral" style={{ fontSize: '0.75rem' }}>{typeLabels[inq.type] || inq.type}</span></td>
                  <td style={{ fontWeight: 600 }}>
                    {inq.quote_amount ? `${inq.currency || 'USD'} ${fmt(inq.quote_amount)}` : '—'}
                  </td>
                  <td>
                    <span className={`badge badge-${statusColors[inq.status] || 'neutral'}`} style={{ textTransform: 'capitalize' }}>{inq.status}</span>
                  </td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: '0.83rem' }}>{inq.assigned_to_name || '—'}</td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{fmtDate(inq.created_at)}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-ghost btn-icon" data-tooltip="View" onClick={() => openView(inq)}>
                        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                      </button>
                      {inq.status !== 'won' && inq.status !== 'lost' && (
                        <button className="btn btn-ghost btn-icon" data-tooltip="Convert to Order" style={{ color: 'var(--success)' }} onClick={() => openConvert(inq)}>
                          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 12h14" /><path d="M12 5l7 7-7 7" /></svg>
                        </button>
                      )}
                      {isManager && inq.is_active === 0 && (
                        <button className="btn btn-ghost btn-icon" data-tooltip="Unfreeze" style={{ color: 'var(--success)' }} onClick={() => handleUnfreeze(inq.id)}>
                          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
                        </button>
                      )}
                      {isManager && inq.is_active !== 0 && (
                        <button className="btn btn-ghost btn-icon" data-tooltip="Freeze" style={{ color: 'var(--error)' }} onClick={() => setDeleteConfirm(inq)}>
                          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={data.page} total={data.total} limit={data.limit} onPageChange={setPage} />
      </div>}

      {/* View Modal */}
      <Modal open={viewModal.open} onClose={() => setViewModal({ open: false })} title="Inquiry Details" size="lg"
        footer={<>
          {viewModal.inquiry && viewModal.inquiry.status !== 'won' && viewModal.inquiry.status !== 'lost' && (
            <button className="btn btn-primary" onClick={() => { setViewModal({ open: false }); openConvert(viewModal.inquiry); }}>
              Convert to Order
            </button>
          )}
          <button className="btn btn-secondary" onClick={() => setViewModal({ open: false })}>Close</button>
        </>}
      >
        {viewModal.inquiry && (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontWeight: 700, fontSize: '1.05rem' }}>{viewModal.inquiry.inquiry_number}</span>
              <span className={`badge badge-${statusColors[viewModal.inquiry.status] || 'neutral'}`} style={{ textTransform: 'capitalize' }}>{viewModal.inquiry.status}</span>
              <span className="badge badge-neutral">{typeLabels[viewModal.inquiry.type] || viewModal.inquiry.type}</span>
            </div>
            <div className="detail-grid" style={{ marginBottom: 16 }}>
              <div className="detail-item"><span className="detail-label">Client</span><span className="detail-value">{viewModal.inquiry.client_name}</span></div>
              <div className="detail-item"><span className="detail-label">Contact</span><span className="detail-value">{viewModal.inquiry.contact_name || '—'}</span></div>
              <div className="detail-item"><span className="detail-label">Subject</span><span className="detail-value">{viewModal.inquiry.subject}</span></div>
              <div className="detail-item"><span className="detail-label">Assigned To</span><span className="detail-value">{viewModal.inquiry.assigned_to_name || '—'}</span></div>
              <div className="detail-item">
                <span className="detail-label">Quote Amount</span>
                <span className="detail-value" style={{ fontWeight: 600 }}>
                  {viewModal.inquiry.quote_amount ? `${viewModal.inquiry.currency} ${fmt(viewModal.inquiry.quote_amount)}` : '—'}
                </span>
              </div>
              <div className="detail-item"><span className="detail-label">Follow-up Date</span><span className="detail-value">{fmtDate(viewModal.inquiry.follow_up_date)}</span></div>
              <div className="detail-item"><span className="detail-label">Inquiry Date</span><span className="detail-value">{fmtDate(viewModal.inquiry.inquiry_date)}</span></div>
              {viewModal.inquiry.catalog_item_name && (
                <div className="detail-item"><span className="detail-label">Catalog Item</span><span className="detail-value">{viewModal.inquiry.catalog_item_name} ({viewModal.inquiry.catalog_number})</span></div>
              )}
            </div>
            {viewModal.inquiry.details && (
              <div style={{ marginBottom: 12 }}>
                <div className="detail-label" style={{ marginBottom: 4 }}>Details</div>
                <div style={{ fontSize: '0.875rem', background: 'var(--surface-2)', padding: '10px 12px', borderRadius: 8, color: 'var(--text-secondary)' }}>
                  {viewModal.inquiry.details}
                </div>
              </div>
            )}
            {viewModal.inquiry.notes && (
              <div>
                <div className="detail-label" style={{ marginBottom: 4 }}>Notes</div>
                <div style={{ fontSize: '0.875rem', background: 'var(--surface-2)', padding: '10px 12px', borderRadius: 8, color: 'var(--text-secondary)' }}>
                  {viewModal.inquiry.notes}
                </div>
              </div>
            )}
            <EntityHistory entity="inquiry" entityId={viewModal.inquiry?.id} />
          </>
        )}
      </Modal>

      {/* Freeze Confirm */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Freeze Inquiry"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
          <button className="btn btn-danger" onClick={handleDelete}>Freeze</button>
        </>}
      >
        <p>Are you sure you want to freeze inquiry <strong>{deleteConfirm?.inquiry_number}</strong>? The record will be locked. You can unfreeze it later.</p>
      </Modal>

      {/* Bulk Upload */}
      <BulkUpload
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onSuccess={load}
        title="Bulk Upload Quotes"
        uploadUrl="/inquiries/bulk"
        templateUrl="/inquiries/template"
        columns={[
          { key: 'client_name', label: 'Client Name', required: true },
          { key: 'subject', label: 'Subject', required: true },
          { key: 'type', label: 'Type' },
          { key: 'details', label: 'Details' },
          { key: 'quote_amount', label: 'Quote Amount' },
          { key: 'currency', label: 'Currency' },
          { key: 'status', label: 'Status' },
          { key: 'follow_up_date', label: 'Follow-up Date' },
          { key: 'assigned_to_email', label: 'Assigned To (Email)' },
        ]}
      />

      {/* Convert to Order Modal */}
      <Modal
        open={convertModal.open}
        onClose={() => !convertModal.saving && setConvertModal({ open: false, inquiry: null, form: emptyConvert(), saving: false, error: '' })}
        title="Convert Inquiry to Order"
        size="lg"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setConvertModal({ open: false, inquiry: null, form: emptyConvert(), saving: false, error: '' })} disabled={convertModal.saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleConvert} disabled={convertModal.saving}>
            {convertModal.saving ? 'Creating…' : 'Create Order & Mark Won'}
          </button>
        </>}
      >
        {convertModal.inquiry && (
          <>
            {/* Inquiry Summary */}
            <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '10px 14px', marginBottom: 18, fontSize: '0.875rem' }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
                <strong>{convertModal.inquiry.inquiry_number}</strong>
                <span className={`badge badge-${statusColors[convertModal.inquiry.status] || 'neutral'}`} style={{ textTransform: 'capitalize' }}>{convertModal.inquiry.status}</span>
              </div>
              <div style={{ color: 'var(--text-secondary)' }}>
                <strong>{convertModal.inquiry.client_name}</strong>
                {convertModal.inquiry.contact_name && ` · ${convertModal.inquiry.contact_name}`}
                {' · '}{convertModal.inquiry.subject}
              </div>
            </div>

            {convertModal.error && (
              <div style={{ background: 'var(--error-bg)', color: 'var(--error)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '0.875rem' }}>
                {convertModal.error}
              </div>
            )}

            {/* PO Document Parser */}
            <div style={{ marginBottom: 14 }}>
              <PODocumentParser
                disabled={convertModal.saving}
                onApply={(fields) => {
                  setConvertModal(m => ({
                    ...m,
                    form: {
                      ...m.form,
                      po_number: fields.po_number || m.form.po_number,
                      po_date: fields.po_date || m.form.po_date,
                      notes: [
                        fields.po_number ? `Ref PO: ${fields.po_number}` : '',
                        fields.buyer_name ? `Buyer: ${fields.buyer_name}` : '',
                        m.form.notes,
                      ].filter(Boolean).join('\n'),
                    },
                  }));
                }}
              />
            </div>

            {/* Order Options */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label className="form-label">Order Type</label>
                <select className="form-control form-select" value={convertModal.form.order_type}
                  onChange={e => setConvertModal(m => ({ ...m, form: { ...m.form, order_type: e.target.value } }))}>
                  {ORDER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Currency</label>
                <select className="form-control form-select" value={convertModal.form.currency}
                  onChange={e => setConvertModal(m => ({ ...m, form: { ...m.form, currency: e.target.value } }))}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Quote Number <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 400 }}>(auto-filled)</span></label>
                <input className="form-control" placeholder="Quote / Inquiry reference" value={convertModal.form.quote_number}
                  onChange={e => setConvertModal(m => ({ ...m, form: { ...m.form, quote_number: e.target.value } }))} />
              </div>
              <div>
                <label className="form-label">Quote Date <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 400 }}>(auto-filled)</span></label>
                <input className="form-control" type="date" value={convertModal.form.quote_date}
                  onChange={e => setConvertModal(m => ({ ...m, form: { ...m.form, quote_date: e.target.value } }))} />
              </div>
              <div>
                <label className="form-label">PO / Ref Number</label>
                <input className="form-control" placeholder="e.g. PO-2025-001" value={convertModal.form.po_number}
                  onChange={e => setConvertModal(m => ({ ...m, form: { ...m.form, po_number: e.target.value } }))} />
              </div>
              <div>
                <label className="form-label">PO Date</label>
                <input className="form-control" type="date" value={convertModal.form.po_date}
                  onChange={e => setConvertModal(m => ({ ...m, form: { ...m.form, po_date: e.target.value } }))} />
              </div>
              <div>
                <label className="form-label">Expected Delivery</label>
                <input className="form-control" type="date" value={convertModal.form.expected_delivery}
                  onChange={e => setConvertModal(m => ({ ...m, form: { ...m.form, expected_delivery: e.target.value } }))} />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="form-label">Notes</label>
              <textarea className="form-control" rows={2} value={convertModal.form.notes}
                onChange={e => setConvertModal(m => ({ ...m, form: { ...m.form, notes: e.target.value } }))} />
            </div>

            {/* Items */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label className="form-label" style={{ margin: 0 }}>Order Items <span style={{ color: 'var(--error)' }}>*</span></label>
                {!convertModal.form.items.length && (
                  <button className="btn btn-secondary btn-sm" onClick={() =>
                    setConvertModal(m => ({ ...m, form: { ...m.form, items: [{ catalog_item_id: convertModal.inquiry.catalog_item_id || '', item_name: convertModal.inquiry.catalog_item_name || '', catalog_number: convertModal.inquiry.catalog_number || '', quantity: convertModal.inquiry.quantity_requested || 1, price: convertModal.inquiry.quote_amount || 0, pack_size: '' }] } }))
                  }>+ Add Item</button>
                )}
              </div>
              {convertModal.form.items.length === 0 && (
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: '10px 0' }}>
                  No catalog item on this inquiry. Click "+ Add Item" to fill in item details manually.
                </div>
              )}
              {convertModal.form.items.map((item, idx) => (
                <div key={idx} style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '12px 14px', marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div style={{ flex: 1, marginRight: 8 }}>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Catalog Item</label>
                      <select className="form-control form-select" style={{ fontSize: '0.82rem' }}
                        value={item.catalog_item_id}
                        onChange={e => {
                          const cat = catalogItems.find(c => c.id === Number(e.target.value));
                          setConvertModal(m => {
                            const items = [...m.form.items];
                            items[idx] = { ...items[idx], catalog_item_id: e.target.value, ...(cat ? { item_name: cat.name, catalog_number: cat.catalog_number || '', price: cat.price || 0 } : {}) };
                            return { ...m, form: { ...m.form, items } };
                          });
                        }}>
                        <option value="">Select item</option>
                        {catalogItems.map(c => (
                          <option key={c.id} value={c.id}>{c.name}{c.catalog_number ? ` [${c.catalog_number}]` : ''}</option>
                        ))}
                      </select>
                    </div>
                    <button className="btn btn-ghost btn-icon btn-sm" style={{ color: 'var(--error)', marginTop: 18 }}
                      onClick={() => setConvertModal(m => ({ ...m, form: { ...m.form, items: m.form.items.filter((_, i) => i !== idx) } }))}>
                      <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 8 }}>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Pack Size</label>
                      <input className="form-control" placeholder="e.g. 100mg" value={item.pack_size}
                        onChange={e => updateConvertItem(idx, 'pack_size', e.target.value)} />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Quantity</label>
                      <input className="form-control" type="number" min="1" value={item.quantity}
                        onChange={e => updateConvertItem(idx, 'quantity', e.target.value)} />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Unit Price</label>
                      <input className="form-control" type="number" min="0" step="0.01" value={item.price}
                        onChange={e => updateConvertItem(idx, 'price', e.target.value)} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, padding: '8px 0', color: 'var(--text-primary)' }}>
                        = {convertModal.form.currency} {fmt(Number(item.price) * Number(item.quantity))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {convertModal.form.items.length > 0 && (
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--primary)' }} onClick={() =>
                  setConvertModal(m => ({ ...m, form: { ...m.form, items: [...m.form.items, { catalog_item_id: '', item_name: '', catalog_number: '', quantity: 1, price: 0, pack_size: '' }] } }))
                }>+ Add Another Item</button>
              )}
            </div>

            {/* Total summary */}
            {convertModal.form.items.length > 0 && (
              <div style={{ textAlign: 'right', paddingTop: 10, borderTop: '1px solid var(--border)', fontWeight: 700, fontSize: '1rem' }}>
                Total: {convertModal.form.currency} {fmt(convertModal.form.items.reduce((s, it) => s + Number(it.price) * Number(it.quantity), 0))}
              </div>
            )}
          </>
        )}
      </Modal>
    </div>
  );
}
